import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import { debounce, includes } from 'lodash';
import { styled } from '@mui/system';
import Autocomplete from '@mui/material/Autocomplete';
import MuiGrid from '@mui/material/Grid';
import CustomInputBase from '@cpg-its/ads.form.custom-input-base';
import Typography from '@cpg-its/ads.common.typography';
import Icon from '@cpg-its/ads.common.icon';
import { blue, darkGrey, grey } from '@cpg-its/ads.constants.colors';
const StyledAutocomplete = styled(Autocomplete)({
    '&.Mui-Autocomplete-listbox': {
        padding: 0,
    },
});
const IconWrapper = styled(MuiGrid)({
    position: 'relative',
    float: 'right',
    bottom: '30px',
    right: '8px',
});
const overrideStyles = ({ color }) => ({
    color: {
        border: `1px solid ${color}`,
        color,
        '& .MuiInputBase-input:focus': {
            border: `1px solid ${color}`,
        },
        '&:hover': {
            border: `1px solid ${color}`,
        },
        '& input, textarea': {
            color,
        },
    },
    narrow: {
        height: '2rem',
        '& input': {
            height: '1rem',
            margin: '2px 1px',
            padding: '5px 0 5px 6px',
        },
    },
    narrowIcon: {
        bottom: '26px !important',
    },
    thickBorder: {
        border: `2px solid ${color || blue} !important`,
    },
    iconPadding: {
        '& input, textarea': {
            paddingRight: '2rem',
        },
    },
});
const SearchSuggestComponent = ({ beenTouched = false, disabled = false, endpointUrl = '', errorStyles, hasError = false, color, narrow, thickBorder, icon = null, iconColor = '', name = '', options = [], onBlur = null, onChange = null, placeholder, searchAction, searchSuggest = '', setFieldTouched = null, setFieldValue = null, testId = undefined, value = {}, }) => {
    const styles = overrideStyles({
        color,
    });
    const inputSx = [
        beenTouched && hasError && errorStyles ? errorStyles.errorInput : {},
        color ? styles.color : {},
        narrow ? styles.narrow : {},
        thickBorder ? styles.thickBorder : {},
        icon ? styles.iconPadding : {},
    ];
    const renderLabel = (option) => {
        if (searchSuggest && option) {
            if (option.name && option.location) {
                return `${option.name}, ${option.location}`;
            }
        }
        return option.name || '';
    };
    const fieldName = typeof searchSuggest === 'string' && includes(searchSuggest, 'employers') ? `${name}.id` : name;
    const [inputValue, setInputValue] = useState(renderLabel(value));
    const [defaultOptions, setDefaultOptions] = useState(options);
    const [totalOptions, setTotalOptions] = useState(options.length);
    const [loading, setLoading] = useState(false);
    const [selectedOption, setSelectedOption] = useState(value);
    const fetchData = async (searchValue) => {
        if (searchValue.length > 1) {
            if (endpointUrl || searchAction) {
                try {
                    let response;
                    let data;
                    setLoading(true);
                    if (endpointUrl) {
                        response = await fetch(endpointUrl);
                    }
                    else if (searchAction) {
                        response = await searchAction(searchValue);
                    }
                    if (response instanceof Response) {
                        data = await response.json();
                    }
                    else {
                        data = response;
                    }
                    const filteredData = data.results.filter((item) => (item.name.toLowerCase().includes(searchValue.toLowerCase())));
                    setTotalOptions(data.totalResults || data.results.length);
                    setDefaultOptions(filteredData);
                    setLoading(false);
                }
                catch (error) {
                    // eslint-disable-next-line no-console
                    console.log('Error:', error);
                    setLoading(false);
                }
            }
            else {
                const filteredData = options.filter((item) => (item.name.toLowerCase().includes(searchValue.toLowerCase())));
                setDefaultOptions(filteredData);
            }
        }
        else {
            setDefaultOptions(options);
        }
    };
    const debouncedFetch = debounce(fetchData, 300);
    const handleBlur = (event) => {
        if (options.length) {
            setDefaultOptions(options);
        }
        if (onBlur) {
            onBlur(event);
        }
        else if (setFieldTouched) {
            setFieldTouched(fieldName, true);
        }
    };
    const handleInputChange = (event, newInputValue) => {
        if (newInputValue === '' && setFieldValue) {
            setFieldValue(fieldName, null);
        }
        setInputValue(newInputValue);
        debouncedFetch(newInputValue);
    };
    const handleChange = (event, selected) => {
        setSelectedOption(selected);
        setInputValue(renderLabel(selected));
        if (onChange) {
            onChange(event);
        }
        else if (setFieldValue) {
            if (selected) {
                if ((typeof searchSuggest === 'boolean' && searchSuggest)) {
                    return setFieldValue(fieldName, selected);
                }
                if (typeof searchSuggest === 'string') {
                    if (includes(searchSuggest, 'employers')) {
                        setFieldValue(fieldName, selected.id);
                        setFieldValue(`${name}.location`, selected.location);
                        setFieldValue(`${name}.name`, selected.name);
                    }
                    if (includes(searchSuggest, 'ClientNumber')) {
                        setFieldValue(`${name}.clientNumber`, selected.clientNumber);
                    }
                }
                return null;
            }
            return setFieldValue(fieldName, {});
        }
        return null;
    };
    const renderIcon = (() => {
        if (icon) {
            return (_jsx(IconWrapper, { children: _jsx(Icon, { color: iconColor, icon: icon }) }));
        }
        return null;
    })();
    const renderMenuItem = ((option) => {
        if (typeof searchSuggest === 'string' && searchSuggest.includes('ClientNumber')) {
            return (_jsxs(MuiGrid, { children: [_jsx(Typography, { color: blue, text: option.name, component: "span", spaceAfter: true }), _jsx(Typography, { color: grey, text: option.location, component: "span" }), _jsx(Typography, { size: "small", text: `Client Number: ${option.clientNumber}` })] }, option.clientNumber || option.id));
        }
        return (_jsxs(React.Fragment, { children: [_jsx(Typography, { color: blue, text: option.name }), _jsx(Typography, { color: blue, text: option.description })] }, option.name || option.id));
    });
    const renderMenuFooter = ((selectableOpts, option) => {
        if (selectableOpts.length > 0 && selectableOpts[selectableOpts.length - 1] === option) {
            return (_jsx(MuiGrid, { sx: {
                    borderTop: `1px solid ${darkGrey}`,
                    padding: '4px 0',
                }, container: true, justifyContent: "center", children: _jsx(Typography, { text: `Results: ${selectableOpts.length} of ${totalOptions}` }) }));
        }
        return null;
    });
    return (_jsxs(_Fragment, { children: [_jsx(StyledAutocomplete, { freeSolo: true, options: defaultOptions, disabled: disabled, disableClearable: true, loading: loading, inputValue: inputValue, onInputChange: handleInputChange, onChange: handleChange, onBlur: handleBlur, getOptionLabel: (option) => renderLabel(option), "data-testid": testId, value: selectedOption, ListboxProps: {
                    sx: {
                        padding: 0,
                    },
                }, renderInput: (params) => {
                    const { InputLabelProps, InputProps: CustomInputProps, ...rest } = params;
                    return (_jsx(CustomInputBase, { sx: inputSx, placeholder: placeholder, ...CustomInputProps, ...rest }));
                }, renderOption: (props, option) => (_jsxs(_Fragment, { children: [_jsxs("li", { ...props, children: [_jsx(MuiGrid, { component: "span", sx: {
                                        width: 14,
                                        height: 14,
                                        flexShrink: 0,
                                        borderRadius: '3px',
                                        mr: 1,
                                        mt: '2px',
                                    } }), renderMenuItem(option)] }), renderMenuFooter(defaultOptions, option)] })) }), renderIcon] }));
};
export default SearchSuggestComponent;
//# sourceMappingURL=search-suggest.js.map